







































































































import {Component, Vue, Watch} from "vue-property-decorator";
import CustomerProfilePage from "@/components/pages/CustomerProfile.vue";
import ESimInstallation from "@/components/pages/ESimInstallation.vue";
import {ResellerInformation} from "@/interfaces/entities/CustomerProfile";
import {DemoScenario, DEMO_SCENARIOS, PLACEHOLDER_LOGO_URL} from "@/demo/whitelabelDemoData";
import {
    parseDemoConfig,
    PreviewPage,
    SAMPLE_BRANDING,
    UNCONFIGURED_BRANDING
} from "@/demo/demoConfig";

const SCENARIO_LABELS: Record<DemoScenario, string> = {
    healthy: "Active plan, plenty of data",
    lowData: "Low data warning",
    notInstalled: "Purchased, not installed yet",
    unlimited: "Unlimited with daily quota",
    expired: "Expired plan"
};

@Component({
    components: {CustomerProfilePage, ESimInstallation}
})
export default class WhitelabelDemo extends Vue {
    private preview: PreviewPage = "profile";
    private scenario: DemoScenario = "healthy";
    private showJson: boolean = false;

    // Hidden unless ?settings is passed: the page's main job is to sit in a reseller's iframe
    // preloaded from the URL, where an editable control panel would be noise at best.
    private settingsVisible: boolean = false;

    // `draft` is what the form edits; `appliedBranding` is what the preview renders, so the
    // pages only restyle when Apply is pressed rather than on every keystroke. Both start from
    // whatever the URL asked for.
    private draft: ResellerInformation = {...UNCONFIGURED_BRANDING};
    private appliedBranding: ResellerInformation | null = {...UNCONFIGURED_BRANDING};

    created() {
        this.applyConfigFromUrl();
    }

    mounted() {
        window.addEventListener("hashchange", this.syncRouteWithHash);
    }

    beforeDestroy() {
        window.removeEventListener("hashchange", this.syncRouteWithHash);
    }

    // Changing only the query does not remount this component, so re-read the config instead of
    // relying on a reload. The URL wins over anything typed into the settings header, which only
    // matters when both are in play.
    @Watch("$route.query")
    private onRouteQueryChanged() {
        this.applyConfigFromUrl();
    }

    // Vue Router's hash history listens for `popstate` whenever pushState is supported, and a
    // fragment edit - retyping the query in the address bar, or a parent page repointing the
    // iframe's hash - raises `hashchange` instead. Without this bridge the router never
    // navigates, so $route stays stale and the watcher above never fires.
    private syncRouteWithHash() {
        const target = window.location.hash.replace(/^#/, "") || "/";
        // Also fires for the replace() below, once the hash has caught up with the route.
        if (target === this.$route.fullPath) return;
        this.$router.replace(target).catch(() => {
            // A navigation the router considers a duplicate rejects; there is nothing to do.
        });
    }

    private applyConfigFromUrl() {
        const config = parseDemoConfig(this.$route.query);
        this.draft = {...config.branding};
        this.appliedBranding = {...config.branding};
        this.preview = config.preview;
        this.scenario = config.scenario;
        this.settingsVisible = config.settingsVisible;
    }

    get scenarioOptions(): Array<{value: DemoScenario; label: string}> {
        return DEMO_SCENARIOS.map((value: DemoScenario) => ({value, label: SCENARIO_LABELS[value]}));
    }

    get isDirty(): boolean {
        const applied = this.appliedBranding ?? UNCONFIGURED_BRANDING;
        return (Object.keys(this.draft) as Array<keyof ResellerInformation>)
            .some((key: keyof ResellerInformation) => this.draft[key] !== applied[key]);
    }

    // Exactly the body a reseller would send to configure a sub-reseller, minus the
    // keys they have not set - the API treats absent keys as "inherit".
    get apiPayload(): string {
        const properties = (Object.keys(this.draft) as Array<keyof ResellerInformation>)
            .reduce((acc: Record<string, string>, key: keyof ResellerInformation) => {
                // Read once so the optional fields narrow; a separate filter() would not.
                const value = this.draft[key];
                if (value) acc[key] = value;
                return acc;
            }, {});
        return JSON.stringify({properties}, null, 2);
    }

    // A fresh object, so the preview's brandingOverride prop actually changes identity
    // and the branded components restyle in place.
    private apply() {
        this.appliedBranding = {...this.draft};
    }

    private loadSample() {
        this.draft = {...SAMPLE_BRANDING};
        this.appliedBranding = {...SAMPLE_BRANDING};
    }

    // Drops back to the unbranded fallbacks, which is what a reseller who has configured
    // nothing actually gets - worth being able to see.
    private clearBranding() {
        this.draft = {...UNCONFIGURED_BRANDING};
        this.appliedBranding = null;
    }

    private usePlaceholderLogo() {
        this.draft.logoUrl = PLACEHOLDER_LOGO_URL;
    }

    private toggleJson() {
        this.showJson = !this.showJson;
    }

    private async copyJson() {
        await navigator.clipboard.writeText(this.apiPayload);
        this.$toastr.success("API payload copied");
    }

    private pickLogoFile() {
        (this.$refs.logoFile as HTMLInputElement).click();
    }

    private onLogoFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            this.draft.logoUrl = String(reader.result);
        };
        reader.readAsDataURL(file);
        // Clear it so picking the same file twice still fires a change event.
        input.value = "";
    }
}
